import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { S3ListResponse, S3DownloadResponse } from '../../types/s3.types';
import { API_URL } from '../../config';
import { tokenManager } from '../../utils/tokenManager';
import { Dispatch } from 'redux';
import { S3_ACTIONS } from '../constants/s3Actions';

// Helper function to get headers
const getHeaders = async () => {
    const token = await tokenManager.getToken();
    const authType = tokenManager.getAuthType();
    return {
        Authorization: `Bearer ${token}`,
        'X-Auth-Type': authType,
        'Content-Type': 'application/json'
    };
};

// Action to list EN validation files
export const fetchEnValidationFiles = createAsyncThunk(
    's3/fetchEnValidationFiles',
    async (prefix: string = '') => {
        const headers = await getHeaders();
        const response = await axios.get<S3ListResponse>(
            `${API_URL}/s3/en-validation?prefix=${encodeURIComponent(prefix)}`,
            { 
                headers,
                withCredentials: true 
            }
        );
        return response.data;
    }
);

// Action to list B2B validation files
export const fetchB2BValidationFiles = createAsyncThunk(
    's3/fetchB2BValidationFiles',
    async (prefix: string = '') => {
        const headers = await getHeaders();
        const response = await axios.get<S3ListResponse>(
            `${API_URL}/s3/b2b-validation?prefix=${encodeURIComponent(prefix)}`,
            { 
                headers,
                withCredentials: true 
            }
        );
        return response.data;
    }
);

// Action to list B2B renewal files
export const fetchB2BRenewalFiles = createAsyncThunk(
    's3/fetchB2BRenewalFiles',
    async (prefix: string = '') => {
        const headers = await getHeaders();
        const response = await axios.get<S3ListResponse>(
            `${API_URL}/s3/b2b-renewal?prefix=${encodeURIComponent(prefix)}`,
            { 
                headers,
                withCredentials: true 
            }
        );
        return response.data;
    }
);

// Action to list Census Error files
export const fetchCensusErrorFiles = createAsyncThunk(
    's3/fetchCensusErrorFiles',
    async (prefix: string = '') => {
        const headers = await getHeaders();
        const response = await axios.get<S3ListResponse>(
            `${API_URL}/s3/census-errors?prefix=${encodeURIComponent(prefix)}`,
            { 
                headers,
                withCredentials: true 
            }
        );
        return response.data;
    }
);

// Action to list Empyrean Error files
export const fetchEmpyreanErrorFiles = createAsyncThunk(
    's3/fetchEmpyreanErrorFiles',
    async (prefix: string = '') => {
        const headers = await getHeaders();
        const response = await axios.get<S3ListResponse>(
            `${API_URL}/s3/empyrean-errors?prefix=${encodeURIComponent(prefix)}`,
            { 
                headers,
                withCredentials: true 
            }
        );
        return response.data;
    }
);

// Action to get download URL
export const getFileDownloadUrl = createAsyncThunk(
    's3/getFileDownloadUrl',
    async ({ bucket, key }: { bucket: string; key: string }) => {
        const headers = await getHeaders();
        const response = await axios.get<S3DownloadResponse>(
            `${API_URL}/s3/download?bucket=${encodeURIComponent(bucket)}&key=${encodeURIComponent(key)}`,
            { 
                headers,
                withCredentials: true 
            }
        );
        return response.data;
    }
);

export const downloadFile = (filePath: string) => async (dispatch: Dispatch) => {
  try {
    const headers = await getHeaders();
    console.log('Download request headers:', headers);
    
    const response = await axios.get(`${API_URL}/s3/download`, {
      params: { file_path: filePath },
      headers: {
        Authorization: headers.Authorization,  // Explicitly set Authorization
        'X-Auth-Type': headers['X-Auth-Type'],  // Explicitly set X-Auth-Type
        'Accept': 'application/octet-stream',
        'Content-Type': 'application/json'
      },
      responseType: 'blob',
      withCredentials: true
    });
    
    // Create blob and download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = filePath.split('/').pop() || 'file';
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Error downloading file:', error);
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorData = JSON.parse(text);
        dispatch({ type: S3_ACTIONS.DOWNLOAD_ERROR, payload: errorData.message || 'Download failed' });
      } catch {
        dispatch({ type: S3_ACTIONS.DOWNLOAD_ERROR, payload: text });
      }
    } else {
      dispatch({ type: S3_ACTIONS.DOWNLOAD_ERROR, payload: error.message });
    }
  }
}; 